import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SERVICES = [
  "Algo Strategy Development",
  "Custom Indicator Development",
  "Custom Screener Development",
];

const SLOTS = [
  "Morning (9 AM – 12 PM)",
  "Afternoon (12 PM – 4 PM)",
  "Evening (4 PM – 7 PM)",
  "Night (7 PM – 10 PM)",
  "Any time on weekend",
];

export default function CodingRequestModal({ open, onClose, preset = "" }) {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    service: "",
    description: "",
    slot: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, service: preset && SERVICES.includes(preset) ? preset : f.service }));
      setDone(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, preset]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isMobile = /^[6-9]\d{9}$/.test(form.mobile.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.service) return;
    if (!isMobile) {
      toast.error("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/coding-requests", form);
      setDone(true);
      toast.success("Request received", {
        description: "Our team will reach out within 24 business hours.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Couldn't submit your request", {
        description: typeof detail === "string" ? detail : "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setForm({ name: "", mobile: "", email: "", service: "", description: "", slot: "" });
      setDone(false);
    }, 200);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-[#0f172a]/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={close}
          data-testid="coding-modal-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-2xl bg-white rounded-t-3xl md:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
            data-testid="coding-modal"
          >
            {/* Header */}
            <div className="px-6 md:px-8 py-5 border-b border-[#e2e8f0] flex items-start justify-between gap-4 bg-[#f8fafc]">
              <div>
                <div className="font-mono text-[10px] font-bold text-[#084392] tracking-[0.22em]">
                  BUILD-FOR-ME REQUEST
                </div>
                <h3 className="font-heading mt-1 text-2xl md:text-3xl tracking-tight font-bold">
                  Tell us what you want to build
                </h3>
              </div>
              <button
                onClick={close}
                data-testid="coding-modal-close"
                className="p-2 rounded-md hover:bg-[#e2e8f0] transition"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 md:px-8 py-6 flex-1">
              {done ? (
                <div className="text-center py-10" data-testid="coding-modal-success">
                  <CheckCircle2 size={56} className="mx-auto text-[#10b981]" />
                  <h4 className="font-heading mt-5 text-2xl font-bold">
                    Request received!
                  </h4>
                  <p className="mt-3 text-[#475569] max-w-md mx-auto">
                    Our team will email you back within <b>24 business hours</b>{" "}
                    with a scoping plan and a fixed quote.
                  </p>
                  <button
                    onClick={close}
                    className="mt-7 inline-flex items-center h-11 px-6 rounded-md bg-[#084392] hover:bg-[#06326e] text-white font-semibold transition"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="coding-form">
                  <Field label="Full name" required>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Rohan Mehta"
                      data-testid="coding-field-name"
                      className={inputCls}
                    />
                  </Field>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Mobile number" required>
                      <input
                        type="tel"
                        required
                        value={form.mobile}
                        onChange={set("mobile")}
                        placeholder="9876543210"
                        maxLength={10}
                        pattern="[6-9][0-9]{9}"
                        data-testid="coding-field-mobile"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Email" required>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@example.com"
                        data-testid="coding-field-email"
                        className={inputCls}
                      />
                    </Field>
                  </div>

                  <Field label="What do you want to build?" required>
                    <select
                      required
                      value={form.service}
                      onChange={set("service")}
                      data-testid="coding-field-service"
                      className={inputCls}
                    >
                      <option value="">Select a service…</option>
                      {SERVICES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Describe your idea" hint="Optional — but the more detail, the better the quote">
                    <textarea
                      rows={4}
                      value={form.description}
                      onChange={set("description")}
                      placeholder="e.g. I want a Nifty straddle that adjusts strikes if VIX crosses 18, with leg-level SL in rupees…"
                      data-testid="coding-field-description"
                      className={`${inputCls} resize-y min-h-[110px]`}
                    />
                  </Field>

                  <Field label="When can we call you?" hint="Optional">
                    <select
                      value={form.slot}
                      onChange={set("slot")}
                      data-testid="coding-field-slot"
                      className={inputCls}
                    >
                      <option value="">No preference</option>
                      {SLOTS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="coding-form-submit"
                      className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-[#084392] hover:bg-[#06326e] text-white font-semibold transition disabled:opacity-70"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending…
                        </>
                      ) : (
                        <>Submit request</>
                      )}
                    </button>
                    <p className="text-xs text-[#94a3b8] sm:max-w-[55%] sm:text-right">
                      By submitting, you agree we may contact you about your
                      request. No spam.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputCls =
  "w-full h-11 rounded-md border border-[#e2e8f0] bg-white px-3.5 text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:border-[#084392] focus:ring-4 focus:ring-[#084392]/10 transition";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-[#0f172a]">
          {label}
          {required && <span className="text-[#ef4444] ml-1">*</span>}
        </span>
        {hint && <span className="text-xs text-[#94a3b8]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
